import { httpGet, httpPost, httpPut, httpDelete, httpPostWithOutToken } from "../../utils/httpMethods";
import { PARTNER_URL, SERVER_PATH } from "../restEndpoint";
import * as Sentry from "@sentry/react";

const { logger } = Sentry;

// Create a new partner application
export const createPartner = async (partnerData) => {
  // Extract only the fields needed for creation (backend generates referralCode and constructs URL)
  const createPayload = {
    name: partnerData.name,
    type: partnerData.type,
    rewardType: partnerData.rewardType,
    rewardValue: partnerData.rewardValue,
    userBonus: partnerData.userBonus,
    totalSignuplimit: partnerData.totalSignuplimit,
    url: partnerData.url // Base URL, backend will construct full URL with referral code
  };
  
  // Add referralCode if provided (optional - backend will auto-generate if not provided)
  if (partnerData.referralCode) {
    createPayload.referralCode = partnerData.referralCode;
  }
  
  // Add expirationDate if provided
  if (partnerData.expirationDate) {
    createPayload.expirationDate = partnerData.expirationDate;
  }
  
  // Add email if provided (for new partners)
  if (partnerData.email) {
    createPayload.email = partnerData.email;
  }
  
  return await httpPost({ url: `${PARTNER_URL}`, payload: createPayload })
    .then((res) => {
      logger.info("Partner created successfully");
      return res;
    })
    .catch((err) => {
      Sentry.captureException(err);
      logger.error("Partner creation error", { error: err.message });
      return err?.response;
    });
};

// Get all partners with pagination and search
export const getAllPartners = async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.search) queryParams.append('search', params.search);
  
  const queryString = queryParams.toString();
  const url = queryString ? `${PARTNER_URL}?${queryString}` : PARTNER_URL;
  
  return await httpGet({ url })
    .then((res) => {
      logger.debug("Get all partners successful");
      return res;
    })
    .catch((err) => {
      Sentry.captureException(err);
      logger.error("Get all partners error", { error: err.message });
      return err?.response;
    });
};

// Get partner by ID
export const getPartnerById = async (id) => {
  return await httpGet({ url: `${PARTNER_URL}/${id}` })
    .then((res) => {
      logger.debug("Get partner by id successful", { id });
      return res;
    })
    .catch((err) => {
      Sentry.captureException(err);
      logger.error("Get partner by id error", { id, error: err.message });
      return err?.response;
    });
};

// Update partner
export const updatePartner = async (id, partnerData) => {
  // For updates, include all fields that can be modified
  const updatePayload = {
    name: partnerData.name,
    type: partnerData.type,
    referralCode: partnerData.referralCode,
    rewardType: partnerData.rewardType,
    rewardValue: partnerData.rewardValue,
    userBonus: partnerData.userBonus,
    totalSignuplimit: partnerData.totalSignuplimit,
    totalEarnings: partnerData.totalEarnings,
    balanceDue: partnerData.balanceDue
  };
  
  // Add expirationDate if provided
  if (partnerData.expirationDate) {
    updatePayload.expirationDate = partnerData.expirationDate;
  }
  
  return await httpPut({ url: `${PARTNER_URL}/${id}`, payload: updatePayload })
    .then((res) => {
      logger.info("Partner updated successfully", { id });
      return res;
    })
    .catch((err) => {
      Sentry.captureException(err);
      logger.error("Partner update error", { id, error: err.message });
      return err?.response;
    });
};

// Delete partner
export const deletePartner = async (id) => {
  try {
    const response = await httpDelete({ url: `${PARTNER_URL}/${id}` });
    const data = await response.json();

    if (response.ok) {
      logger.info("Partner deleted successfully", { id });
      return { status: response.status, data };
    } else {
      throw new Error(data.message || "Failed to delete partner");
    }
  } catch (err) {
    Sentry.captureException(err);
    logger.error("Partner deletion error", { error: err.message });
    return { status: 500, data: { message: err.message } };
  }
};

// PARTNER REQUEST FUNCTIONS

// Submit a partner request (public - no auth required)
export const submitPartnerRequest = async (requestData) => {
  try {
    const response = await httpPostWithOutToken({ 
      url: `${SERVER_PATH}/api/partners/partner-requests`, 
      payload: requestData 
    });
    return response;
  } catch (err) {
    // Return error response structure for consistent handling
    return {
      status: err.response?.status || 500,
      data: err.response?.data || { message: err.message || "An error occurred while submitting your request" }
    };
  }
};

// Get all partner requests (admin)
export const getPartnerRequests = async (status) => {
  const url = status 
    ? `${SERVER_PATH}/api/partners/partner-requests?status=${status}`
    : `${SERVER_PATH}/api/partners/partner-requests`;
  return await httpGet({ url })
    .then((res) => {
      logger.debug("Get all partner requests successful");
      return res;
    })
    .catch((err) => {
      Sentry.captureException(err);
      logger.error("Get all partner requests error", { error: err.message });
      return err?.response;
    });
};

// Get partner request by ID (admin)
export const getPartnerRequestById = async (id) => {
  return await httpGet({ url: `${SERVER_PATH}/api/partners/partner-requests/${id}` })
    .then((res) => {
      logger.debug("Get partner request by id successful", { id });
      return res;
    })
    .catch((err) => {
      Sentry.captureException(err);
      logger.error("Get partner request by id error", { id, error: err.message });
      return err?.response;
    });
};

// Approve partner request (admin)
export const approvePartnerRequest = async (id, approvalData) => {
  return await httpPost({ 
    url: `${SERVER_PATH}/api/partners/partner-requests/${id}/approve`, 
    payload: approvalData 
  })
    .then((res) => {
      logger.info("Partner request approved successfully", { id });
      return res;
    })
    .catch((err) => {
      Sentry.captureException(err);
      logger.error("Partner request approval error", { id, error: err.message });
      return err?.response;
    });
};

// Reject partner request (admin)
export const rejectPartnerRequest = async (id, rejectionData) => {
  return await httpPost({ 
    url: `${SERVER_PATH}/api/partners/partner-requests/${id}/reject`, 
    payload: rejectionData 
  })
    .then((res) => {
      logger.info("Partner request rejected successfully", { id });
      return res;
    })
    .catch((err) => {
      Sentry.captureException(err);
      logger.error("Partner request rejection error", { id, error: err.message });
      return err?.response;
    });
};

// Send partner invitation email (admin)
export const sendPartnerInvitation = async (email) => {
  return await httpPost({ 
    url: `${SERVER_PATH}/api/partners/send-invitation`, 
    payload: { email } 
  })
    .then((res) => {
      logger.info("Partner invitation sent successfully", { email });
      return res;
    })
    .catch((err) => {
      Sentry.captureException(err);
      logger.error("Partner invitation error", { email, error: err.message });
      return err?.response;
    });
};